import { Color, Element, Stripe } from '@thive/canvas';
import { Areas, Rectangle } from '@thive/fddm-types';

import { NumUtils } from '@/modules/common/utils';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import {
  createArrowElement,
  createRackImage,
  createSegmentArrowElement,
} from '@/modules/canvas/elements/imageElements';
import { elementFactory } from '@/modules/canvas/factories';
import { UnifiedVehicleDetails } from '@/modules/vehicles/types/internal';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { modifyColor, AREAS_COLORS_MAP } from '@/modules/floorplanDesign/mappers/colors';
import { areaToLayerTag } from '@modules/layers/helpers';
import { segmentsToPathPoints, segmentToRectangle } from './mapperHelpers';

// temporary solution to make sure that newer elements are on top
const Z_OFFSET = 1e-8;

const AREA_INTERACTIVITY = {
  hoverable: true,
  draggable: true,
  rotatable: true,
  scalable: true,
  selectable: true,
  snappable: true,
  clickable: false,
};

const TRANSPARENT_STROKE = { color: '#00000000', width: 0 };

const createAreaRectangleElement = ({
  id,
  rectangle,
  z,
  color,
  tag,
  layerTag,
  stripe,
}: {
  id: string;
  rectangle: Rectangle;
  z: number;
  color: string;
  tag: string;
  layerTag: string;
  stripe?: Stripe;
}): Element =>
  elementFactory.buildRectangleElement({
    id,
    rectangle,
    z,
    color,
    stroke: TRANSPARENT_STROKE,
    stripe,
    interactivity: AREA_INTERACTIVITY,
    tags: [tag, layerTag],
  });

export const areaMapper = (
  areas: Areas,
  vehicleDetails: UnifiedVehicleDetails[],
  zoom: number,
): Element[] => {
  console.debug('[areaMapper] zoom used for icons', zoom);
  let currentZ = Z_OFFSET;

  const elements: Element[] = [];
  const { highwayAreas, rackingAreas, singularStationAreas, stationaryAreas, manualAreas } = areas;

  highwayAreas.forEach((area) => {
    currentZ += Z_OFFSET;

    const highwayPoiSpec = area.highwayPoiSpecs[0];
    const firstSegment = area.segments[0];
    if (!highwayPoiSpec || !firstSegment) return;

    const vehicleWidth =
      vehicleDetails.find((vehicle) => vehicle.id === highwayPoiSpec.vehicleTypeId)?.width ?? 0;
    const laneGap = highwayPoiSpec.verticalSpacing.minGap;
    const margin = highwayPoiSpec.verticalSpacing.minMargin;

    if (area.segments.length === 1) {
      elements.push(
        elementFactory.buildHighwayElement({
          id: area.id,
          rectangle: segmentToRectangle(firstSegment),
          z: Z_INDEX_MAP[FDDM_TAG.ANGLED_HIGHWAY_AREA] + currentZ,
          color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
          vehicleWidth: NumUtils.mmToMeters(vehicleWidth),
          laneGap,
          margin,
          laneColor: modifyColor(AREAS_COLORS_MAP.LANE, true),
          interactivity: AREA_INTERACTIVITY,
          tags: [FDDM_TAG.HIGHWAY_AREA, areaToLayerTag(area)],
        }),
        createArrowElement(area, zoom),
      );

      return;
    }

    elements.push(
      elementFactory.buildAngledHighwayElement({
        id: area.id,
        points: segmentsToPathPoints(area.segments),
        z: Z_INDEX_MAP[FDDM_TAG.HIGHWAY_AREA] + currentZ,
        stroke: {
          color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
          width: firstSegment.leftSideWidth + firstSegment.rightSideWidth,
        },
        color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
        vehicleWidth: NumUtils.mmToMeters(vehicleWidth),
        laneGap,
        margin,
        laneColor: modifyColor(AREAS_COLORS_MAP.LANE, true),
        interactivity: {
          draggable: true,
          rotatable: false,
          selectable: true,
          editable: true,
          snappable: true,
          scalable: false,
        },
        tags: [FDDM_TAG.HIGHWAY_AREA, FDDM_TAG.ANGLED_HIGHWAY_AREA, areaToLayerTag(area)],
      }),
    );

    area.segments.forEach((segment) => {
      elements.push(createSegmentArrowElement(area, segment, zoom));
    });
  });

  rackingAreas.forEach((area) => {
    currentZ += Z_OFFSET;

    elements.push(
      createAreaRectangleElement({
        id: area.id,
        rectangle: area.rectangle,
        z: Z_INDEX_MAP[FDDM_TAG.RACKING_AREA] + currentZ,
        color: modifyColor(AREAS_COLORS_MAP[area.rackingLoadHandlingType], true),
        tag: FDDM_TAG.RACKING_AREA,
        layerTag: areaToLayerTag(area),
      }),
      createRackImage(area, zoom),
    );
  });

  singularStationAreas.forEach((area) => {
    currentZ += Z_OFFSET;

    elements.push(
      createAreaRectangleElement({
        id: area.id,
        rectangle: area.rectangle,
        z: Z_INDEX_MAP[FDDM_TAG.SINGULAR_STATION_AREA] + currentZ,
        color: modifyColor(AREAS_COLORS_MAP[area.loadHandlingType], true),
        tag: FDDM_TAG.SINGULAR_STATION_AREA,
        layerTag: areaToLayerTag(area),
      }),
    );
  });

  stationaryAreas.forEach((area) => {
    currentZ += Z_OFFSET;

    elements.push(
      createAreaRectangleElement({
        id: area.id,
        rectangle: area.rectangle,
        z: Z_INDEX_MAP[FDDM_TAG.STATIONARY_AREA] + currentZ,
        color: modifyColor(AREAS_COLORS_MAP[area.stationaryType], true),
        tag: FDDM_TAG.STATIONARY_AREA,
        layerTag: areaToLayerTag(area),
      }),
    );
  });

  manualAreas.forEach((area) => {
    currentZ += Z_OFFSET;

    elements.push(
      createAreaRectangleElement({
        id: area.id,
        rectangle: area.rectangle,
        z: Z_INDEX_MAP[FDDM_TAG.MANUAL_AREA] + currentZ,
        color: modifyColor(AREAS_COLORS_MAP.MANUAL, true),
        stripe: {
          angle: 45,
          width: 5,
          color: Color.fromHex(AREAS_COLORS_MAP.MANUAL),
        },
        tag: FDDM_TAG.MANUAL_AREA,
        layerTag: areaToLayerTag(area),
      }),
    );
  });

  return elements;
};
